import React, { useEffect, useState } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";

import BodyText from "Common/BodyText";
import { Card } from "Common/Card";
import Icon from "Common/Icon";

import AlternateDocumentsCardSection from "./documentPicker/AlternateDocumentsCardSection";
import AlternateDocumentsModal from "./documentPicker/AlternateDocumentsModal";
import { useMainDocumentState, useUserFlagsState, useAlternativeDocumentsState } from "./documentPicker/hooks";
import MainDocumentSection from "./documentPicker/MainDocumentSection";
import { parseDocType, parseDocKey, DocumentPickerContext, bem, useDocumentPickerContext } from "./documentPicker/utils";
import WageDocumentIssueModal from "./documentPicker/WageDocumentIssueModal";

const Loading = ({ text }) => (
  <div className={bem("UploadContainer")}>
    <Icon icon="spinner" spin="pulse" size="2x" mx="auto" py={2} />
    {text ?? (
      <BodyText agent pb={2}>
        {text}
      </BodyText>
    )}
  </div>
);

const CardContent = () => {
  const {
    mainDocumentState: { uploading, syncingDocument, fullfilled: mainDocumentFullfilled },

    alternativeDocumentsState: { setAltDocsCardIsOpen, showDocumentAlternatives },

    userFlagsState: { persistedAlternativeType },
    validateDocument,
  } = useDocumentPickerContext();

  // The `showDocumentAlternatives` flag is needed here since
  // different accounts of the same company might be in different
  // A/B test groups.
  useEffect(() => {
    if (showDocumentAlternatives && !mainDocumentFullfilled && persistedAlternativeType) setAltDocsCardIsOpen(true);
  }, []);

  return (
    <>
      {uploading || syncingDocument ? <Loading text=" Uploading File..." /> : null}
      <MainDocumentSection />
      {showDocumentAlternatives && <AlternateDocumentsCardSection />}
      {validateDocument && <WageDocumentIssueModal />}
      {showDocumentAlternatives && <AlternateDocumentsModal />}
    </>
  );
};

export const DocumentPicker = ({
  index,
  originalFilename,
  fileInputName,
  title,
  description,
  sampleDocuments,
  meta,
  primaryAlternatives,
  secondaryAlternatives,
  matchedPrimaryDocuments,
  matchedSecondaryDocuments,
  onDocumentUpload,
  onDocumentUploadSuccess,
  validateDocument,
  showWhyRequired = false,
}) => {
  const mainDocumentState = useMainDocumentState({
    fileInputName,
    originalFilename,
    onDocumentUpload,
  });

  const { uploading, filename, error, setError, setUploadedInvalidDocument, syncingDocument, fullfilled: mainDocumentFullfilled } = mainDocumentState;

  const userFlagsState = useUserFlagsState({ fileInputName });
  const { isLoading: userFlagsLoading } = userFlagsState;

  const alternativeDocumentsState = useAlternativeDocumentsState({
    matchedPrimaryDocuments,
    matchedSecondaryDocuments,
    primaryAlternatives,
    secondaryAlternatives,
    userFlagsState,
  });

  const { fullfilled: alternativeFullfilled, altDocsCardIsOpen } = alternativeDocumentsState;

  const [validating, setValidating] = useState(!!validateDocument);
  useEffect(() => {
    if (validateDocument) {
      const handleValidation = async () => {
        const type = parseDocType(fileInputName);
        const key = parseDocKey(fileInputName);
        const doc = { type, key };

        const response = await validateDocument(doc);
        const data = await response.json();
        const error = data?.errors?.[0]?.detail || null;
        setError(error);
        setUploadedInvalidDocument(!!error);
        setValidating(false);
      };
      handleValidation();
    }
  }, []);

  const uploadDocsComplete = altDocsCardIsOpen ? alternativeFullfilled : mainDocumentFullfilled;

  const processing = userFlagsLoading || validating || uploading || syncingDocument;

  const classNames = classnames(bem("container"), {
    [bem("container", "fileMissing")]: !filename || altDocsCardIsOpen,
    [bem("container", "fileError")]: error && !altDocsCardIsOpen,
    [bem("container", "filePresent")]: !uploading && uploadDocsComplete,
  });

  const docType = parseDocType(fileInputName);
  const docKey = parseDocKey(fileInputName);

  const context = {
    description,
    fileInputName,
    originalFilename,
    index,
    meta,
    primaryAlternatives,
    sampleDocuments,
    secondaryAlternatives,
    title,
    onDocumentUpload,
    onDocumentUploadSuccess,
    validateDocument,
    pickerState: {
      uploadDocsComplete,
      processing,
    },
    docType,
    mainDocumentState,
    userFlagsState,
    alternativeDocumentsState,
    docKey,
    showWhyRequired,
  };

  return (
    <DocumentPickerContext.Provider value={context}>
      <Card theme="flat" data-testid="DocumentPicker" data-value={`${docType}[${docKey}]`} className={classNames}>
        {userFlagsLoading || validating ? <Loading /> : <CardContent />}
      </Card>
    </DocumentPickerContext.Provider>
  );
};

DocumentPicker.propTypes = {
  fileInputName: PropTypes.string.isRequired,
  originalFilename: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  sampleDocuments: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
    }),
  ).isRequired,
};

export default DocumentPicker;
